// Customer-facing Scene images: resized copies, decoded once and kept for the session.
const RESIZABLE = /\/api\/storage\/files\/.+\.(png|jpe?g|webp)$/i;
const decoded = new Map();

export const sceneWidth = () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 768 : 1024);

export const sceneSrc = (url) => {
  if (!url) return url;
  const base = url.split('?')[0];
  return RESIZABLE.test(base) && !url.includes('?') ? `${url}?w=${sceneWidth()}` : url;
};

export const sceneThumb = (url, w = 256) => (url && RESIZABLE.test(url.split('?')[0]) ? `${url.split('?')[0]}?w=${w}` : url);

// Resolves when the image is decoded; repeated calls for the same URL reuse the first result.
// The bytes are kept as an in-memory object URL so revisits never re-download, whatever the cache headers.
const MAX_KEPT = 80;
const decodeImage = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(im));
  im.onerror = reject;
  im.src = src;
});

export const preloadScene = (src) => {
  if (!src) return Promise.resolve({ url: null, aspect: null });
  if (decoded.has(src)) return decoded.get(src);
  const aspectOf = (im) => (im.naturalHeight && im.naturalWidth ? im.naturalHeight / im.naturalWidth : null);
  const p = fetch(src)
    .then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); })
    .then((b) => { const obj = URL.createObjectURL(b); return decodeImage(obj).then((im) => ({ url: src, src: obj, aspect: aspectOf(im) })); })
    .catch(() => decodeImage(src).then((im) => ({ url: src, aspect: aspectOf(im) })).catch(() => { decoded.delete(src); return { url: src, aspect: null }; }));
  decoded.set(src, p);
  if (decoded.size > MAX_KEPT) {
    const [oldest, op] = decoded.entries().next().value;
    decoded.delete(oldest);
    // Free memory only if that copy is not on screen
    op.then((r) => r.src && !document.querySelector(`img[src="${r.src}"]`) && URL.revokeObjectURL(r.src));
  }
  return p;
};

export const isSceneReady = (src) => decoded.has(src);

const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// The Scene a color opens on (same choice as the visualizer's default room render).
export const defaultSceneUrl = (c) => {
  const raw = c?.room_visualizations?.[c?.default_room] || Object.values(c?.room_visualizations || {}).find(Boolean) || c?.room_visualization_url || c?.ai_visualization_url;
  return raw ? sceneSrc(abs(raw)) : null;
};

// Quietly preload the default Scenes for a collection's colors, a few at a time when the browser is idle.
export const preloadCollectionScenes = (colors = [], limit = 24) => {
  const urls = [...new Set(colors.map(defaultSceneUrl).filter(Boolean))].filter((u) => !decoded.has(u)).slice(0, limit);
  let cancelled = false;
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
  const next = () => {
    if (cancelled || !urls.length) return;
    Promise.all(urls.splice(0, 3).map(preloadScene)).then(() => idle(next));
  };
  idle(next);
  return () => { cancelled = true; };
};
